import { useLazyQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'

import { SearchCheckboxesV2 } from 'components/SearchCheckboxesV2'
import { useDebouncedQuery } from 'components/v2/Autocomplete'
import { GET_COMPANIES_SUGGESTIONS } from 'features/Companies/queries-legacy'
import { GTCompanySuggestion, GTCompanySuggestionsResponse } from 'gql/types.generated'

const messages = defineMessages({
  allCompanies: {
    id: 'companies.checkboxes.all',
    defaultMessage: 'All companies'
  },
  error: {
    id: 'companies.checkboxes.error',
    defaultMessage: 'Error loading your companies'
  },
  actionOnError: {
    id: 'companies.checkboxes.actionOnError',
    defaultMessage: 'Try again'
  },
  empty: {
    id: 'companies.checkboxes.empty',
    defaultMessage: 'No companies found'
  },
  allOption: {
    id: 'companies.checkboxes.all',
    defaultMessage: 'All companies'
  },
  searchPlaceholder: {
    id: 'companies.checkboxes.searchPlaceholder',
    defaultMessage: 'Search company'
  }
})

interface IMultiCompanyCheckboxesProps {
  values: GTCompanySuggestion[]
  onChange: (companies: GTCompanySuggestion[]) => void
}

export const MultiCompanyCheckboxes = ({
  values = [],
  onChange,
  ...props
}: IMultiCompanyCheckboxesProps): ReactElement => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')
  const [getCompanies, { data, loading, error }] = useLazyQuery<{ companySuggestions: GTCompanySuggestionsResponse }>(
    GET_COMPANIES_SUGGESTIONS
  )
  const { isDebouncing } = useDebouncedQuery({
    query: getCompanies,
    pattern
  })

  const getInputValue = (): string => {
    if (!isEmpty(values)) {
      return values
        .map((company): string => {
          return company.name
        })
        .join(', ')
    }

    return intl.formatMessage(messages.allCompanies)
  }

  const handleChange = (companies: GTCompanySuggestion[], pattern = ''): void => {
    setPattern(pattern)

    onChange(companies)
  }

  return (
    <SearchCheckboxesV2
      items={data?.companySuggestions.items || []}
      loading={loading || isDebouncing}
      error={error && intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      values={values}
      inputValue={getInputValue()}
      allOption={intl.formatMessage(messages.allOption)}
      getItemLabel={(company: GTCompanySuggestion): string => company.name}
      getTagLabel={(company: GTCompanySuggestion): string => company.name}
      searchPlaceholder={intl.formatMessage(messages.searchPlaceholder)}
      {...props}
      onChange={handleChange}
    />
  )
}
